import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, AlertTriangle } from 'lucide-react';
import { calculateGameStatus, getTimeUntilGame } from '@/utils/gameStatusUtils';

interface CountdownTimerProps {
  gameDate: string;
  gameTime: string;
  gameTitle: string;
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  gameDate,
  gameTime,
  gameTitle
}) => {
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isUrgent, setIsUrgent] = useState<boolean>(false);

  useEffect(() => {
    const updateCountdown = () => {
      // Use UTC as default for countdown timer to ensure consistency
      const gameTimezone = 'UTC';
      const gameStatus = calculateGameStatus(gameDate, gameTime, undefined, 2, gameTimezone);
      
      if (gameStatus === 'upcoming') {
        const timeUntil = getTimeUntilGame(gameDate, gameTime, gameTimezone);
        setTimeRemaining(timeUntil);
        
        // Check if less than 1 hour remaining
        const gameDateTime = new Date(`${gameDate}T${gameTime}`);
        const now = new Date();
        const hoursRemaining = (gameDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
        setIsUrgent(hoursRemaining <= 1);
      } else {
        setTimeRemaining('');
      }
    };

    // Update immediately
    updateCountdown();

    // Update every minute
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [gameDate, gameTime]);

  if (!timeRemaining) {
    return null;
  }

  return (
    <Card className={`border-2 ${isUrgent ? 'border-destructive bg-destructive/5' : 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isUrgent ? (
              <AlertTriangle className="h-5 w-5 text-destructive" />
            ) : (
              <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            )}
            <div>
              <h4 className={`font-semibold ${isUrgent ? 'text-destructive' : 'text-yellow-800 dark:text-yellow-200'}`}>
                {isUrgent ? 'URGENT: Form Expires Soon!' : 'Form Expiry Countdown'}
              </h4>
              <p className={`text-sm ${isUrgent ? 'text-destructive/80' : 'text-yellow-700 dark:text-yellow-300'}`}>
                Complete your form before the game starts
              </p>
            </div>
          </div>
          
          <div className="text-right">
            <Badge 
              variant={isUrgent ? 'destructive' : 'secondary'} 
              className={`font-mono text-lg px-3 py-1 ${
                isUrgent ? '' : 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
              }`}
            >
              {timeRemaining}
            </Badge>
            <div className={`text-xs mt-1 ${isUrgent ? 'text-destructive/60' : 'text-yellow-600 dark:text-yellow-400'}`}>
              until "{gameTitle}" starts
            </div>
          </div>
        </div>
        
        {isUrgent && (
          <div className="mt-3 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
            <p className="text-sm text-destructive font-medium">
              ⚠️ This form will become unavailable when the game starts. Complete it now to avoid losing access!
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};